// Core
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import LogRocket from 'logrocket';
import { Intercom } from 'ng-intercom';
import { NgxPendoService } from 'ngx-pendo';
import { Observable, throwError, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

// App
import { AppState } from '@app/core/reducers';
import { environment } from '@app/env-service';
import { EPendoTrackEvent, IPendo } from '@app/shared/pendo/pendo.types';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { ServiceBellService } from 'app/shared/services/service-bell.service';
import { TenantInfoService } from 'app/shared/services/tenant-info.service';
import { UpscopeService } from 'app/shared/services/upscope.service';

import { LogIn, UserLoaded } from '../store/auth.actions';
import { User } from '../store/auth.model';

import { JwtHelperService } from './jwt-helper.service';
// Plugins

declare let pendo :IPendo; // allow referencing the global pendo var declared by pendo

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  regWGoogle :boolean = false;
  loginUser :any;

  constructor(
    private _ServiceBell :ServiceBellService,
    private _Upscope :UpscopeService,
    private http :HttpClient,
    private helper :JwtHelperService,
    private store :Store<AppState>,
    private router :Router,
    private route :ActivatedRoute,
    private featureToggleService :FeatureToggleService,
    private intercom :Intercom,
    private tenantInfoService :TenantInfoService,
    protected ngxPendoService :NgxPendoService,
  ) {
    // this.checkToken();

    if (
      window.location.href.indexOf('auth') === -1 &&
      !window.location.pathname.replace(/^\/+/, '').startsWith('public')
    ) {
      this.checkToken();
    }
  }

  checkToken() {
    const token = localStorage.getItem(environment.accessTokenKey);
    if (this.isTokenValid(token)) {
      this.loginByToken(token);
      this.refreshTokenAndLogin();
    } else {
      const refreshToken = localStorage.getItem(environment.refreshTokenKey);
      if (this.isTokenValid(refreshToken)) {
        this.loginByToken(refreshToken);
        this.refreshTokenAndLogin();
      } else {
        this.logout();
        this.router.navigate(['/auth/login'], {
          queryParams: { returnTo: window.location.pathname || '/' },
        });
      }
    }
  }

  refreshTokenAndLogin() {
    this.refreshAccess().subscribe((data :any) => {
      const decoded = this.helper.decodeToken(data.access_token);
      this.loginByToken(data.access_token);
    });
  }

  async loginByToken(token :any) {
    const decoded = this.helper.decodeToken(token);
    this.store.dispatch(new LogIn(true));
    this.store.dispatch(new UserLoaded(decoded.user));
    this.loginUser = decoded.user;
    this.thirdPartyIdentity(decoded.user);
    if (!environment.tenantId) {
      await environment.init();
    }
    this.featureToggleService.initSdk(this.loginUser.id);
  }

  isTokenValid(token) {
    if (token && this.helper.isTokenValid(token)) {
      const isExpired = this.helper.isTokenExpired(token);
      if (isExpired) {
        return false;
      }

      return true;
    }

    return false;
  }

  refreshAccess() :Observable<any> {
    const credentials = {
      token: this.getRefreshToken(),
    };

    return this.http.post(`${environment.url}/auth/refresh`, credentials).pipe(
      tap((tokens :any) => {
        this.registerAuthTokens(tokens);
      }),
      catchError((error :any) => {
        this.logout();
        this.router.navigateByUrl('/auth/login');

        return throwError(error);
      }),
    );
  }

  loginWithB2bPortalToken(token :string) :Observable<any> {
    const payload :any = {
      token: token,
    };

    return this.http.post(`${environment.url}/auth/login/token`, payload).pipe(
      tap((data :any) => {
        if (data.user && data.tokens) {
          this.store.dispatch(new UserLoaded(<User>data.user));
          this.registerAuthTokens(data.tokens);
          this.loginUser = data.user;
          this.thirdPartyIdentity(data.user);
        }
      }),
    );
  }

  loginWithAdminToken(token :string) :Observable<any> {
    const payload :any = {
      token: token,
    };

    return this.http.post(`${environment.url}/auth/enterprise/login`, payload).pipe(
      tap((data :any) => {
        if (data.user && data.tokens) {
          this.store.dispatch(new UserLoaded(<User>data.user));
          this.registerAuthTokens(data.tokens);
          this.loginUser = data.user;
          this.thirdPartyIdentity(data.user);
        }
      }),
    );
  }

  login(credentials :any) :Observable<any> {
    return this.http.post(`${environment.url}/auth/login`, credentials).pipe(
      tap((data :any) => {
        this.store.dispatch(new UserLoaded(<User>data.user));
        this.registerAuthTokens(data.tokens);
        this.loginUser = data.user;
        this.thirdPartyIdentity(data.user);
      }),
    );
  }

  thirdPartyIdentity(user) {
    const { tenantId, clientConfig } = environment;
    const account = clientConfig.name;
    const { email, firstname, id, lastname } = user;
    const name = `${user.firstname} ${user.lastname}`;
    const uniqueId = `${tenantId}#${id}:${email}`;

    this.intercom.update({
      name,
      email,
      account,
      company: {
        id: tenantId,
        name: account,
      },
    });

    LogRocket.identify('jmsum4/luminous-v1', {
      name,
      email,
      account,
    });

    this._ServiceBell.identify(uniqueId, {
      email,
      displayName: name,
      account,
      tenantId,
    });

    this._Upscope.identify({
      uniqueId,
      identities: [account, email, name, tenantId],
    });

    setTimeout(() => {
      this.ngxPendoService.initialize(
        {
          id: user.email,
          name: user.firstname + ' ' + user.lastname,
          email: user.email,
        },
        {
          id: environment.clientConfig.name,
          name: environment.clientConfig.name,
        },
      );

      pendo?.track?.(EPendoTrackEvent.USER_LOGGED_IN, {
        windowWidth: JSON.stringify(window.innerWidth),
        windowHeight: JSON.stringify(window.innerHeight),
      });
    }, 1000);
  }

  updateProfileModal(user :any) :Observable<any> {
    this.store.dispatch(new UserLoaded(<User>user));
    this.refreshTokenAndLogin();
    this.loginUser = user;

    return of(null);
  }

  updateProfile(newProfile :any) :Observable<any> {
    return of(null);
  }

  logout() {
    localStorage.removeItem(environment.accessTokenKey);
    localStorage.removeItem(environment.refreshTokenKey);
    localStorage.removeItem('layoutConfig');
    this.regWGoogle = false;

    if (this.featureToggleService.isActive('utah_UTAH-76_service_bell_widget')) {
      this._ServiceBell.disconnect();
    }
  }

  loginByIdToken(idToken :string) :Observable<any> {
    const credentials = {
      idToken: idToken,
    };

    return this.http.post(`${environment.url}/auth/login`, credentials).pipe(
      tap((data :any) => {
        this.store.dispatch(new UserLoaded(<User>data.user));
        this.registerAuthTokens(data.tokens);
      }),
    );
  }

  forgotPassword(email :string) {
    const credentials = { email: email };

    return this.http.post(`${environment.url}/auth/forgot`, credentials);
  }
  savePassword(credentials :any) :Observable<any> {
    return this.http.post(`${environment.url}/auth/password`, credentials);
  }

  decodeToken(token :any) {
    const decoded = this.helper.decodeToken(token);

    return decoded;
  }

  registerAuthTokens(tokens :any) {
    localStorage.setItem(environment.accessTokenKey, tokens.access_token);
    localStorage.setItem(environment.refreshTokenKey, tokens.refresh_token);
  }

  getAccessToken() {
    return localStorage.getItem(environment.accessTokenKey);
  }

  getRefreshToken() {
    return localStorage.getItem(environment.refreshTokenKey);
  }

  getUser() {
    const data = this.decodeToken(this.getAccessToken());

    return data.user;
  }
  getLanguage() {
    const data = this.decodeToken(this.getAccessToken());

    return data.lang;
  }
  setLanguage(lang) {
    let tz :string = 'America/Denver';
    if (typeof Intl === 'object' && typeof Intl.DateTimeFormat === 'function') {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    return this.http
      .post(`${environment.url}/auth/language`, {
        language: lang,
        timezone: tz,
      })
      .pipe(
        tap((data :any) => {
          this.registerAuthTokens(data.tokens);
        }),
      );
  }
  updatePassword(password) {
    return this.http.post(`${environment.url}/auth/chpassword`, password);
  }
}
